import { Link, NavLink, useLocation } from "react-router-dom";
import { Sun, Moon, ShoppingCart, User, Menu, X, LayoutDashboard } from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import { useAdminAuth } from "../admin/auth";
import { useEffect, useState } from "react";
import HeaderSearchTrigger from "./HeaderSearchTrigger";
import TopContactBar from "./TopContactBar";

const Header = () => {
  const { theme, toggle } = useTheme();
  const { count } = useCart();
  const { user: adminUser } = useAdminAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: "Home" },
    { to: "/parts", label: "Auto Parts" },
    { to: "/tyres", label: "Tyres" },
    { to: "/mobile-fitting", label: "Services" },
    { to: "/recovery", label: "Recovery" },
    { to: "/order/track", label: "Track Order" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <>
      <TopContactBar />
      <header className="vd-header sticky top-0 z-50" data-testid="site-header">
      <div className="vd-container flex items-center justify-between" style={{ height: 96 }}>
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <Logo />
        </Link>

        <nav className="hidden md:flex items-center gap-8" data-testid="primary-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) => `vd-nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <HeaderSearchTrigger />
          <button
            onClick={toggle}
            className="p-2 rounded-md hover:bg-[var(--bg-secondary)] transition-colors"
            aria-label="Toggle theme"
            data-testid="theme-toggle-btn"
          >
            {theme === "dark" ? <Sun size={20} color="var(--text-heading)" /> : <Moon size={20} color="var(--text-heading)" />}
          </button>
          <Link to="/checkout" className="relative p-2 rounded-md hover:bg-[var(--bg-secondary)] transition-colors" data-testid="cart-icon-link">
            <ShoppingCart size={20} color="var(--text-heading)" />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 text-[10px] font-bold rounded-full flex items-center justify-center"
                style={{ background: "var(--primary-red)", color: "#fff", minWidth: 18, height: 18, padding: "0 5px" }}
                data-testid="cart-count-badge"
              >{count}</span>
            )}
          </Link>
          <Link
            to={adminUser ? "/admin/dashboard" : "/admin/login"}
            className="p-2 rounded-md hover:bg-[var(--bg-secondary)] transition-colors hidden md:inline-flex"
            aria-label={adminUser ? "Admin dashboard" : "Owner login"}
            title={adminUser ? "Admin dashboard" : "Owner login"}
            data-testid="profile-btn"
          >
            {adminUser ? <LayoutDashboard size={20} color="var(--primary-red)" /> : <User size={20} color="var(--text-heading)" />}
          </Link>
          <button
            className="md:hidden p-2 rounded-md hover:bg-[var(--bg-secondary)]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} color="var(--text-heading)" /> : <Menu size={22} color="var(--text-heading)" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t" style={{ borderColor: "var(--border-color)", background: "var(--bg-primary)" }} data-testid="mobile-nav-panel">
          <div className="vd-container py-4 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) => `vd-nav-link py-2 ${isActive ? "active" : ""}`}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >{l.label}</NavLink>
            ))}
            <NavLink
              to={adminUser ? "/admin/dashboard" : "/admin/login"}
              className="vd-nav-link py-2 inline-flex items-center gap-2 mt-1 border-t pt-3"
              style={{ borderColor: "var(--border-color)" }}
              data-testid="mobile-nav-owner-login"
            >
              {adminUser ? <LayoutDashboard size={16} color="var(--primary-red)" /> : <User size={16} />}
              {adminUser ? "Admin dashboard" : "Owner login"}
            </NavLink>
          </div>
        </div>
      )}
    </header>
    </>
  );
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_auto-solutions-stage/artifacts/1glpuwnd_Photoroom_20260515_125444.png";

const Logo = () => (
  <img
    src={LOGO_URL}
    alt="ValueDrive Auto Solutions"
    className="vd-logo-img select-none"
    data-testid="brand-logo"
  />
);

export default Header;
